<template>
  <q-page class="live-eval-page">
    <div class="live-eval-container">
      <!-- Loading state -->
      <div v-if="loading" class="live-eval-loading">
        <q-spinner-dots color="amber" size="48px" />
        <p class="loading-text">Cargando instrumentos...</p>
      </div>

      <!-- Error state -->
      <div v-else-if="error" class="live-eval-empty">
        <q-icon name="error_outline" size="48px" :style="{ color: 'var(--oa-danger)' }" />
        <p class="empty-text">{{ error }}</p>
        <q-btn
          no-caps
          unelevated
          label="Reintentar"
          :style="{ background: 'var(--oa-gold-full)', color: 'var(--color-marble-cream)', fontWeight: '600', borderRadius: '8px' }"
          @click="loadInstrumentData"
        />
      </div>

      <!-- Empty criteria -->
      <div v-else-if="!loading && flatSteps.length === 0" class="live-eval-empty">
        <q-icon name="warning" size="48px" :style="{ color: 'var(--oa-text-disabled)' }" />
        <p class="empty-text">No hay criterios cargados.</p>
      </div>

      <!-- Main flow -->
      <template v-else>
        <!-- Header -->
        <div class="live-eval-header">
          <h1 class="eval-title">
            {{ isFullSet ? 'Evaluacion de Sesion Completa' : INSTRUMENT_META[selectedInstrument || 'live_observation']?.label }}
          </h1>
          <p v-if="isFullSet" class="eval-subtitle">
            4 instrumentos &middot; {{ totalCriteria }} criterios &middot; Resultado CNAP combinado
          </p>

          <!-- Progress -->
          <div class="eval-progress-section">
            <div class="eval-progress-labels">
              <span>
                {{ isSummary ? 'Resumen final' : `Paso ${currentStep + 1} de ${totalSteps}` }}
                <template v-if="currentInstrumentSection && !isSummary">
                  &middot; {{ INSTRUMENT_META[currentInstrumentSection.instrument]?.shortLabel }}
                </template>
              </span>
              <span class="eval-progress-pct">{{ completionPct }}%</span>
            </div>
            <q-linear-progress
              :value="completionPct / 100"
              color="amber"
              track-color="grey-9"
              rounded
              size="6px"
            />
          </div>
        </div>

        <!-- Coach selector for new evaluations -->
        <div v-if="isNew" class="setup-card">
          <p class="setup-label">Datos de la evaluacion</p>
          <div class="setup-grid">
            <div class="setup-field-full">
              <label class="field-label">Entrenador a evaluar</label>
              <q-select
                v-model="selectedCoachId"
                :options="coachOptions"
                option-value="value"
                option-label="label"
                emit-value
                map-options
                outlined
                dense
                dark
                placeholder="Seleccionar entrenador..."
                :input-style="{ color: 'var(--oa-text-primary)' }"
                popup-content-class="q-dark"
              >
                <template #option="scope">
                  <q-item v-bind="scope.itemProps">
                    <q-item-section>
                      <q-item-label>{{ scope.opt.label }}</q-item-label>
                      <q-item-label caption>{{ scope.opt.role }}</q-item-label>
                    </q-item-section>
                  </q-item>
                </template>
              </q-select>
            </div>
            <template v-if="selectedCoachData">
              <div class="setup-field">
                <span class="field-tiny-label">Nombre</span>
                <p class="field-value">{{ selectedCoachData.firstName }} {{ selectedCoachData.lastName }}</p>
              </div>
              <div class="setup-field">
                <span class="field-tiny-label">Rol</span>
                <p class="field-value">{{ (selectedCoachData.role || '').toUpperCase() }}</p>
              </div>
            </template>
            <div class="setup-field">
              <span class="field-tiny-label">Fecha</span>
              <p class="field-value">{{ todayFormatted }}</p>
            </div>
            <div class="setup-field">
              <span class="field-tiny-label">Modo</span>
              <p class="field-value">
                {{ isFullSet ? 'Set completo (4 instrumentos)' : INSTRUMENT_META[selectedInstrument || 'live_observation']?.label }}
              </p>
            </div>
          </div>
        </div>

        <!-- Instrument tabs (full set mode) -->
        <div v-if="isFullSet" class="instrument-tabs">
          <button
            v-for="section in instrumentSections"
            :key="section.instrument"
            class="instrument-tab"
            :class="{
              'tab-current': currentStep >= section.startIdx && currentStep <= section.endIdx && !isSummary,
              'tab-complete': isSectionComplete(section),
            }"
            @click="currentStep = section.startIdx"
          >
            <q-icon
              :name="isSectionComplete(section) ? 'check_circle' : INSTRUMENT_META[section.instrument]?.icon"
              size="14px"
            />
            {{ INSTRUMENT_META[section.instrument]?.shortLabel }}
            <span class="tab-weight">{{ INSTRUMENT_META[section.instrument]?.weight }}</span>
          </button>
          <button
            class="instrument-tab"
            :class="{ 'tab-current': isSummary }"
            @click="currentStep = flatSteps.length"
          >
            <q-icon name="send" size="14px" />
            Resumen
          </button>
        </div>

        <!-- Area stepper within current instrument -->
        <div v-if="!isSummary" class="area-stepper">
          <button
            v-for="step in currentInstrumentSteps"
            :key="step.area.id"
            class="area-step-btn"
            :class="{
              'step-current': flatSteps.indexOf(step) === currentStep,
              'step-complete': isAreaComplete(flatSteps.indexOf(step)),
            }"
            @click="currentStep = flatSteps.indexOf(step)"
          >
            <q-icon
              :name="isAreaComplete(flatSteps.indexOf(step)) ? 'check_circle' : 'radio_button_unchecked'"
              size="12px"
            />
            {{ step.area.name }}
          </button>
        </div>

        <!-- Step content: Area criteria -->
        <div v-if="!isSummary && currentFlatStep" class="criteria-card">
          <div class="criteria-header">
            <h2 class="criteria-area-name">{{ currentFlatStep.area.name }}</h2>
            <p class="criteria-count">
              {{ areaCompletion[currentStep]?.filled ?? 0 }} de {{ areaCompletion[currentStep]?.total ?? 0 }} criterios
            </p>
          </div>

          <div
            v-for="(criterion, cIdx) in currentFlatStep.area.criteria"
            :key="criterion.id"
            class="criterion-row"
            :class="{ 'criterion-border': cIdx < currentFlatStep.area.criteria.length - 1 }"
          >
            <div class="criterion-top">
              <p class="criterion-name">{{ criterion.name }}</p>
              <button
                class="notes-toggle"
                :class="{ 'notes-active': showNotes[criterion.id] }"
                @click="showNotes[criterion.id] = !showNotes[criterion.id]"
              >
                <q-icon name="chat_bubble_outline" size="14px" />
              </button>
            </div>
            <p v-if="criterion.description" class="criterion-desc">{{ criterion.description }}</p>

            <!-- Scale buttons (1-5) -->
            <div v-if="criterion.criterionType === 'scale'" class="score-buttons">
              <button
                v-for="val in 5"
                :key="val"
                class="score-btn"
                :class="{ [`score-btn-${val}-active`]: responses[criterion.id]?.value === val }"
                :style="responses[criterion.id]?.value !== val ? { background: 'var(--oa-bg-3)', color: 'var(--oa-text-disabled)' } : {}"
                @click="setResponse(criterion.id, val)"
              >
                {{ val }}
              </button>
            </div>
            <p
              v-if="criterion.criterionType === 'scale' && responses[criterion.id]?.value"
              class="scale-label-text"
            >
              {{ SCALE_LABELS[responses[criterion.id].value] }}
            </p>

            <!-- Binary buttons (No / Parcial / Si) -->
            <div v-if="criterion.criterionType !== 'scale'" class="score-buttons">
              <button
                v-for="opt in BINARY_OPTIONS"
                :key="opt.value"
                class="score-btn"
                :class="{ [opt.activeClass]: responses[criterion.id]?.value === opt.value }"
                :style="responses[criterion.id]?.value !== opt.value ? { background: 'var(--oa-bg-3)', color: 'var(--oa-text-disabled)' } : {}"
                @click="setResponse(criterion.id, opt.value)"
              >
                {{ opt.label }}
              </button>
            </div>

            <!-- Optional notes -->
            <q-input
              v-if="showNotes[criterion.id]"
              :model-value="responses[criterion.id]?.notes ?? ''"
              @update:model-value="setNotes(criterion.id, $event as string)"
              type="textarea"
              rows="2"
              placeholder="Notas adicionales..."
              outlined
              dense
              dark
              class="criterion-notes"
              :input-style="{ color: 'var(--oa-text-primary)', fontSize: '13px' }"
            />
          </div>
        </div>

        <!-- Summary step -->
        <div v-if="isSummary" class="summary-section">
          <!-- Per-instrument summary -->
          <div
            v-for="section in instrumentSections"
            :key="section.instrument"
            class="summary-instrument-card"
          >
            <div class="summary-instrument-header">
              <div class="summary-instrument-left">
                <q-icon
                  :name="INSTRUMENT_META[section.instrument]?.icon"
                  size="16px"
                  :style="{ color: isSectionComplete(section) ? 'var(--oa-success)' : 'var(--oa-gold-full)' }"
                />
                <h3 class="summary-instrument-name">{{ INSTRUMENT_META[section.instrument]?.label }}</h3>
                <span class="summary-weight-badge">{{ INSTRUMENT_META[section.instrument]?.weight }}</span>
              </div>
              <q-icon
                v-if="isSectionComplete(section)"
                name="check_circle"
                size="16px"
                :style="{ color: 'var(--oa-success)' }"
              />
              <span v-else class="summary-progress-text">
                {{ getSectionFilled(section) }}/{{ getSectionCriteria(section) }}
              </span>
            </div>
            <div class="summary-area-list">
              <div
                v-for="(step, idx) in flatSteps.slice(section.startIdx, section.endIdx + 1)"
                :key="step.area.id"
                class="summary-area-row"
              >
                <div class="summary-area-left">
                  <q-icon
                    :name="isAreaComplete(section.startIdx + idx) ? 'check_circle' : 'warning'"
                    size="12px"
                    :style="{ color: isAreaComplete(section.startIdx + idx) ? 'var(--oa-success)' : 'var(--oa-gold-full)' }"
                  />
                  <span class="summary-area-name">{{ step.area.name }}</span>
                </div>
                <span class="summary-area-count">
                  {{ areaCompletion[section.startIdx + idx]?.filled }}/{{ areaCompletion[section.startIdx + idx]?.total }}
                </span>
              </div>
            </div>
          </div>

          <!-- Feedback -->
          <div class="summary-feedback-card">
            <label class="summary-feedback-label">Feedback general (opcional)</label>
            <q-input
              v-model="feedback"
              type="textarea"
              rows="4"
              placeholder="Observaciones generales sobre la sesion..."
              outlined
              dark
              :input-style="{ color: 'var(--oa-text-primary)', fontSize: '14px' }"
            />
          </div>

          <!-- Total -->
          <div class="summary-total-card">
            <p class="summary-total-label">Total completado</p>
            <p class="summary-total-value">{{ totalFilled }}/{{ totalCriteria }}</p>
            <div class="summary-total-bar">
              <div class="summary-total-bar-fill" :style="{ width: completionPct + '%' }" />
            </div>
          </div>
        </div>

        <!-- Navigation -->
        <div class="eval-nav">
          <button
            class="nav-btn nav-prev"
            :disabled="currentStep === 0"
            @click="currentStep = Math.max(0, currentStep - 1)"
          >
            <q-icon name="chevron_left" size="16px" />
            Anterior
          </button>

          <q-btn
            v-if="isSummary"
            no-caps
            unelevated
            :loading="isSubmitting"
            :disable="totalFilled < totalCriteria || isSubmitting || (isNew && !selectedCoachId)"
            :label="isSubmitting ? (submitProgress || 'Enviando...') : 'Enviar Evaluacion'"
            icon="send"
            :style="{
              background: totalFilled >= totalCriteria && !isSubmitting && (!isNew || selectedCoachId)
                ? 'var(--oa-gold-full)' : 'var(--oa-bg-3)',
              color: totalFilled >= totalCriteria && !isSubmitting && (!isNew || selectedCoachId)
                ? 'var(--color-marble-cream)' : 'var(--oa-text-disabled)',
              fontWeight: '700',
              borderRadius: '12px',
              padding: '8px 24px',
            }"
            @click="handleSubmit"
          />

          <button
            v-else
            class="nav-btn nav-next"
            @click="currentStep = Math.min(totalSteps - 1, currentStep + 1)"
          >
            {{ currentStep === flatSteps.length - 1 ? 'Revisar y Enviar' : 'Siguiente' }}
            <q-icon name="chevron_right" size="16px" />
          </button>
        </div>
      </template>
    </div>
  </q-page>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, reactive } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useQuasar } from 'quasar';
import { api } from 'boot/axios';

// ---------- Types ----------
interface EvalCriterion {
  id: string;
  name: string;
  criterionType: string;
  maxScore: number;
  sortOrder: number;
  isActive: boolean;
  areaId: string;
  description: string | null;
}

interface EvalArea {
  id: string;
  name: string;
  instrument: string;
  weight: string;
  sortOrder: number;
  isActive: boolean;
  criteria: EvalCriterion[];
}

type LiveInstrumentType = 'live_observation' | 'performance_binary' | 'protocol_checklist' | 'routine_execution';

interface FlatStep {
  type: 'area';
  instrument: LiveInstrumentType;
  area: EvalArea;
  areaIndex: number;
  instrumentIndex: number;
}

interface InstrumentSection {
  instrument: LiveInstrumentType;
  startIdx: number;
  endIdx: number;
}

interface CoachItem {
  id: string;
  firstName: string;
  lastName: string;
  role: string;
  avatarUrl?: string | null;
}

// ---------- Constants ----------
const INSTRUMENT_ORDER: LiveInstrumentType[] = [
  'live_observation', 'performance_binary', 'protocol_checklist', 'routine_execution',
];

const INSTRUMENT_META: Record<string, { label: string; shortLabel: string; icon: string; weight: string }> = {
  live_observation: { label: 'Observacion en Vivo', shortLabel: 'Observacion', icon: 'visibility', weight: '35%' },
  performance_binary: { label: 'Desempeno Operativo', shortLabel: 'Desempeno', icon: 'fact_check', weight: '25%' },
  protocol_checklist: { label: 'Adherencia a Protocolos', shortLabel: 'Protocolos', icon: 'verified', weight: '20%' },
  routine_execution: { label: 'Ejecucion de Rutinas', shortLabel: 'Rutinas', icon: 'list_alt', weight: '20%' },
};

const SCALE_LABELS: Record<number, string> = {
  1: 'Deficiente',
  2: 'Necesita mejora',
  3: 'Aceptable',
  4: 'Bueno',
  5: 'Excelente',
};

const BINARY_OPTIONS = [
  { value: 0, label: 'No', activeClass: 'score-btn-binary-no' },
  { value: 0.5, label: 'Parcial', activeClass: 'score-btn-binary-parcial' },
  { value: 1, label: 'Si', activeClass: 'score-btn-binary-si' },
];

// ---------- Router / Quasar ----------
const $q = useQuasar();
const route = useRoute();
const router = useRouter();

const cycleId = computed(() => route.params.cycleId as string);
const instrumentParam = computed(() => (route.query.instrument as LiveInstrumentType | undefined) || null);
const isNew = computed(() => cycleId.value === 'new');
const isFullSet = computed(() => !instrumentParam.value);

// ---------- State ----------
const loading = ref(true);
const error = ref('');
const coaches = ref<CoachItem[]>([]);
const selectedCoachId = ref('');
const selectedInstrument = computed(() => instrumentParam.value || 'live_observation');
const feedback = ref('');
const isSubmitting = ref(false);
const submitProgress = ref('');
const currentStep = ref(0);
const responses = reactive<Record<string, { value: number; notes?: string }>>({});
const showNotes = reactive<Record<string, boolean>>({});

// Instrument areas data
const instrumentAreas = reactive<Record<string, EvalArea[]>>({});

// ---------- Computed ----------
const coachOptions = computed(() =>
  coaches.value.map((c) => ({
    value: c.id,
    label: `${c.firstName} ${c.lastName}`,
    role: (c.role || '').toUpperCase(),
  })),
);

const selectedCoachData = computed(() =>
  coaches.value.find((c) => c.id === selectedCoachId.value) || null,
);

const todayFormatted = computed(() =>
  new Date().toLocaleDateString('es-AR', { day: 'numeric', month: 'long', year: 'numeric' }),
);

const flatSteps = computed<FlatStep[]>(() => {
  const steps: FlatStep[] = [];
  if (isFullSet.value) {
    INSTRUMENT_ORDER.forEach((inst, instIdx) => {
      const areas = instrumentAreas[inst] || [];
      areas.forEach((area, aIdx) => {
        steps.push({ type: 'area', instrument: inst, area, areaIndex: aIdx, instrumentIndex: instIdx });
      });
    });
  } else {
    const inst = selectedInstrument.value as LiveInstrumentType;
    const areas = instrumentAreas[inst] || [];
    areas.forEach((area, aIdx) => {
      steps.push({ type: 'area', instrument: inst, area, areaIndex: aIdx, instrumentIndex: 0 });
    });
  }
  return steps;
});

const instrumentSections = computed<InstrumentSection[]>(() => {
  const sections: InstrumentSection[] = [];
  if (isFullSet.value) {
    INSTRUMENT_ORDER.forEach((inst) => {
      const startIdx = flatSteps.value.findIndex((s) => s.instrument === inst);
      if (startIdx === -1) return;
      let endIdx = startIdx;
      for (let i = startIdx; i < flatSteps.value.length; i++) {
        if (flatSteps.value[i].instrument === inst) endIdx = i;
        else break;
      }
      sections.push({ instrument: inst, startIdx, endIdx });
    });
  } else {
    const inst = selectedInstrument.value as LiveInstrumentType;
    if (flatSteps.value.length > 0) {
      sections.push({ instrument: inst, startIdx: 0, endIdx: flatSteps.value.length - 1 });
    }
  }
  return sections;
});

const totalSteps = computed(() => flatSteps.value.length + 1); // +1 for summary
const isSummary = computed(() => currentStep.value >= flatSteps.value.length);
const currentFlatStep = computed(() => flatSteps.value[currentStep.value] || null);

const totalCriteria = computed(() =>
  flatSteps.value.reduce((sum, s) => sum + s.area.criteria.length, 0),
);

const totalFilled = computed(() => Object.keys(responses).length);

const completionPct = computed(() =>
  totalCriteria.value > 0
    ? Math.round((totalFilled.value / totalCriteria.value) * 100)
    : 0,
);

const areaCompletion = computed(() => {
  const result: Record<number, { total: number; filled: number }> = {};
  flatSteps.value.forEach((step, idx) => {
    const total = step.area.criteria.length;
    const filled = step.area.criteria.filter((c) => responses[c.id] !== undefined).length;
    result[idx] = { total, filled };
  });
  return result;
});

const currentInstrumentSection = computed(() =>
  instrumentSections.value.find(
    (s) => currentStep.value >= s.startIdx && currentStep.value <= s.endIdx,
  ) || null,
);

const currentInstrumentSteps = computed(() => {
  if (isFullSet.value && currentFlatStep.value) {
    return flatSteps.value.filter((s) => s.instrument === currentFlatStep.value!.instrument);
  }
  return flatSteps.value;
});

// ---------- Methods ----------
function isAreaComplete(stepIdx: number): boolean {
  const comp = areaCompletion.value[stepIdx];
  return !!comp && comp.filled === comp.total;
}

function isSectionComplete(section: InstrumentSection): boolean {
  for (let i = section.startIdx; i <= section.endIdx; i++) {
    if (!isAreaComplete(i)) return false;
  }
  return true;
}

function getSectionCriteria(section: InstrumentSection): number {
  let total = 0;
  for (let i = section.startIdx; i <= section.endIdx; i++) {
    total += flatSteps.value[i].area.criteria.length;
  }
  return total;
}

function getSectionFilled(section: InstrumentSection): number {
  let filled = 0;
  for (let i = section.startIdx; i <= section.endIdx; i++) {
    filled += flatSteps.value[i].area.criteria.filter((c) => responses[c.id] !== undefined).length;
  }
  return filled;
}

function setResponse(criterionId: string, value: number) {
  if (responses[criterionId]) {
    responses[criterionId].value = value;
  } else {
    responses[criterionId] = { value };
  }
}

function setNotes(criterionId: string, notes: string) {
  if (!responses[criterionId]) {
    responses[criterionId] = { value: 0, notes };
  } else {
    responses[criterionId].notes = notes;
  }
}

async function loadInstrumentData() {
  loading.value = true;
  error.value = '';

  try {
    const instrumentsToLoad = isFullSet.value ? INSTRUMENT_ORDER : [selectedInstrument.value as LiveInstrumentType];

    const results = await Promise.all(
      instrumentsToLoad.map((inst) =>
        api.get(`/supervision/instruments/${inst}/areas`).then((res) => ({ inst, areas: res.data as EvalArea[] })),
      ),
    );

    for (const { inst, areas } of results) {
      instrumentAreas[inst] = areas;
    }

    if (isNew.value) {
      const { data: coachList } = await api.get('/supervision/coaches');
      coaches.value = coachList;
    }
  } catch (err) {
    console.error(err);
    error.value = 'Error al cargar los instrumentos de evaluacion';
  } finally {
    loading.value = false;
  }
}

async function handleSubmit() {
  if (totalFilled.value < totalCriteria.value) return;
  isSubmitting.value = true;
  submitProgress.value = '';

  try {
    if (isFullSet.value) {
      // Submit one cycle per instrument
      let lastCycleId = '';
      for (const section of instrumentSections.value) {
        const inst = section.instrument;
        submitProgress.value = `Enviando ${INSTRUMENT_META[inst]?.shortLabel}...`;

        // Collect responses for this instrument's criteria
        const instResponses: Record<string, { value: number; notes?: string }> = {};
        for (let i = section.startIdx; i <= section.endIdx; i++) {
          const step = flatSteps.value[i];
          for (const c of step.area.criteria) {
            if (responses[c.id]) {
              instResponses[c.id] = responses[c.id];
            }
          }
        }

        const { data: cycle } = await api.post('/supervision/cycles', {
          coachId: selectedCoachId.value,
          instrument: inst,
          occasion: 'scheduled',
        });

        const isLast = section === instrumentSections.value[instrumentSections.value.length - 1];

        await api.post(`/supervision/cycles/${cycle.id}/submit`, {
          responses: instResponses,
          feedback: isLast ? feedback.value || undefined : undefined,
        });

        lastCycleId = cycle.id;
      }

      submitProgress.value = 'Redirigiendo...';
      router.push(`/evaluations/results/${lastCycleId}`);
    } else {
      // Single instrument mode
      let targetCycleId = cycleId.value;
      if (isNew.value && selectedCoachId.value) {
        const { data: cycle } = await api.post('/supervision/cycles', {
          coachId: selectedCoachId.value,
          instrument: instrumentParam.value || 'live_observation',
          occasion: 'scheduled',
        });
        targetCycleId = cycle.id;
      }

      await api.post(`/supervision/cycles/${targetCycleId}/submit`, {
        responses,
        feedback: feedback.value || undefined,
      });

      router.push(`/evaluations/results/${targetCycleId}`);
    }
  } catch (err) {
    console.error(err);
    $q.notify({
      message: 'Error al enviar la evaluacion',
      color: 'negative',
      icon: 'error',
      position: 'top',
    });
    isSubmitting.value = false;
    submitProgress.value = '';
  }
}

// ---------- Lifecycle ----------
onMounted(() => {
  loadInstrumentData();
});
</script>

<style scoped lang="scss">
.live-eval-page {
  padding: 24px;
}

.live-eval-container {
  max-width: 800px;
  margin: 0 auto;
}

// === Loading / Empty ===
.live-eval-loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 80px 20px;
}

.loading-text {
  font-size: var(--oa-text-sm);
  color: var(--oa-text-muted);
  margin: 16px 0 0 0;
}

.live-eval-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 64px 32px;
  text-align: center;
  background: var(--oa-bg-2);
  border-radius: var(--oa-radius-xl);
  border: 1px solid color-mix(in srgb, var(--oa-border) 50%, transparent);
}

.empty-text {
  color: var(--oa-text-secondary);
  margin: 12px 0 0 0;
}

// === Header ===
.live-eval-header {
  margin-bottom: 24px;
}

.eval-title {
  font-size: var(--oa-text-xl);
  font-weight: 600;
  color: var(--oa-text-primary);
  margin: 0;
}

.eval-subtitle {
  font-size: var(--oa-text-xs);
  color: var(--oa-text-muted);
  margin: 4px 0 0 0;
}

.eval-progress-section {
  margin-top: 12px;
}

.eval-progress-labels {
  display: flex;
  justify-content: space-between;
  font-size: 11px;
  color: var(--oa-text-muted);
  margin-bottom: 6px;
}

.eval-progress-pct {
  font-family: monospace;
}

// === Setup card ===
.setup-card {
  background: var(--oa-bg-2);
  border: 1px solid var(--oa-border);
  border-radius: var(--oa-radius-xl);
  padding: 20px;
  margin-bottom: 20px;
}

.setup-label {
  font-size: var(--oa-text-xs);
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: var(--oa-text-muted);
  margin: 0 0 12px 0;
}

.setup-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
}

.setup-field-full {
  grid-column: 1 / -1;
}

.setup-field {
  display: flex;
  flex-direction: column;
}

.field-label {
  display: block;
  font-size: 11px;
  font-weight: 500;
  color: var(--oa-text-muted);
  margin-bottom: 4px;
}

.field-tiny-label {
  font-size: 10px;
  color: var(--oa-text-disabled);
}

.field-value {
  font-size: var(--oa-text-sm);
  font-weight: 500;
  color: var(--oa-text-primary);
  margin: 2px 0 0 0;
}

// === Instrument tabs ===
.instrument-tabs {
  display: flex;
  gap: 4px;
  overflow-x: auto;
  padding-bottom: 4px;
  margin-bottom: 12px;
}

.instrument-tab {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 12px;
  border-radius: 8px;
  font-size: 11px;
  font-weight: 500;
  white-space: nowrap;
  border: 1px solid transparent;
  background: var(--oa-bg-3);
  color: var(--oa-text-muted);
  cursor: pointer;
  transition: all 0.15s ease;

  &.tab-current {
    background: color-mix(in srgb, var(--oa-gold-full) 12%, transparent);
    color: var(--oa-gold-full);
    border-color: color-mix(in srgb, var(--oa-gold-full) 30%, transparent);
  }

  &.tab-complete:not(.tab-current) {
    background: color-mix(in srgb, var(--oa-success) 8%, transparent);
    color: var(--oa-success);
  }
}

.tab-weight {
  font-size: 9px;
  opacity: 0.6;
}

// === Area stepper ===
.area-stepper {
  display: flex;
  align-items: center;
  gap: 4px;
  overflow-x: auto;
  padding-bottom: 4px;
  margin-bottom: 16px;
}

.area-step-btn {
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 4px 10px;
  border-radius: 999px;
  font-size: 10px;
  font-weight: 500;
  white-space: nowrap;
  border: none;
  background: var(--oa-bg-3);
  color: var(--oa-text-disabled);
  cursor: pointer;
  transition: all 0.15s ease;

  &.step-current {
    background: var(--oa-gold-full);
    color: var(--color-marble-cream);
  }

  &.step-complete:not(.step-current) {
    background: color-mix(in srgb, var(--oa-success) 12%, transparent);
    color: var(--oa-success);
  }
}

// === Criteria card ===
.criteria-card {
  background: var(--oa-bg-2);
  border: 1px solid var(--oa-border);
  border-radius: 16px;
  overflow: hidden;
  margin-bottom: 16px;
}

.criteria-header {
  padding: 16px 20px;
  background: var(--oa-bg-3);
  border-bottom: 1px solid var(--oa-border);
}

.criteria-area-name {
  font-size: var(--oa-text-sm);
  font-weight: 600;
  color: var(--oa-text-primary);
  margin: 0;
}

.criteria-count {
  font-size: 11px;
  color: var(--oa-text-muted);
  margin: 2px 0 0 0;
}

.criterion-row {
  padding: 16px 20px;
}

.criterion-border {
  border-bottom: 1px solid var(--oa-border);
}

.criterion-top {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
}

.criterion-name {
  font-size: var(--oa-text-sm);
  color: var(--oa-text-primary);
  flex: 1;
  margin: 0;
}

.criterion-desc {
  font-size: 12px;
  color: var(--oa-text-muted);
  margin: 4px 0 0 0;
}

.notes-toggle {
  padding: 4px;
  border-radius: 4px;
  border: none;
  background: transparent;
  color: var(--oa-text-disabled);
  cursor: pointer;
  transition: color 0.15s ease;
  flex-shrink: 0;

  &.notes-active {
    color: var(--oa-gold-full);
  }
}

// === Score buttons ===
.score-buttons {
  display: flex;
  gap: 8px;
  margin-top: 10px;
}

.score-btn {
  flex: 1;
  padding: 8px 4px;
  border-radius: 12px;
  font-size: var(--oa-text-sm);
  font-weight: 500;
  border: none;
  cursor: pointer;
  transition: all 0.15s ease;
}

// Scale 1-5 active states
.score-btn-1-active {
  background: #ef4444 !important;
  color: var(--color-marble-cream) !important;
}

.score-btn-2-active {
  background: #fb923c !important;
  color: var(--color-marble-cream) !important;
}

.score-btn-3-active {
  background: #fbbf24 !important;
  color: var(--color-marble-cream) !important;
}

.score-btn-4-active {
  background: #4ade80 !important;
  color: var(--color-marble-cream) !important;
}

.score-btn-5-active {
  background: var(--oa-gold-full) !important;
  color: var(--color-marble-cream) !important;
}

// Binary active states
.score-btn-binary-no {
  background: #ef4444 !important;
  color: var(--color-marble-cream) !important;
}

.score-btn-binary-parcial {
  background: #fbbf24 !important;
  color: var(--color-marble-cream) !important;
}

.score-btn-binary-si {
  background: #4ade80 !important;
  color: var(--color-marble-cream) !important;
}

.scale-label-text {
  font-size: 10px;
  color: var(--oa-text-disabled);
  margin: 4px 0 0 0;
}

.criterion-notes {
  margin-top: 8px;
}

// === Summary ===
.summary-section {
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin-bottom: 16px;
}

.summary-instrument-card {
  background: var(--oa-bg-2);
  border: 1px solid var(--oa-border);
  border-radius: var(--oa-radius-xl);
  padding: 16px;
}

.summary-instrument-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;
}

.summary-instrument-left {
  display: flex;
  align-items: center;
  gap: 8px;
}

.summary-instrument-name {
  font-size: var(--oa-text-sm);
  font-weight: 600;
  color: var(--oa-text-primary);
  margin: 0;
}

.summary-weight-badge {
  font-size: 10px;
  padding: 2px 6px;
  border-radius: 4px;
  background: var(--oa-bg-3);
  color: var(--oa-text-muted);
}

.summary-progress-text {
  font-size: 12px;
  color: var(--oa-text-muted);
}

.summary-area-list {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.summary-area-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 4px 0;
}

.summary-area-left {
  display: flex;
  align-items: center;
  gap: 8px;
}

.summary-area-name {
  font-size: var(--oa-text-xs);
  color: var(--oa-text-secondary);
}

.summary-area-count {
  font-size: 10px;
  font-family: monospace;
  color: var(--oa-text-disabled);
}

.summary-feedback-card {
  background: var(--oa-bg-2);
  border: 1px solid var(--oa-border);
  border-radius: var(--oa-radius-xl);
  padding: 16px;
}

.summary-feedback-label {
  display: block;
  font-size: var(--oa-text-sm);
  font-weight: 500;
  color: var(--oa-text-primary);
  margin-bottom: 8px;
}

.summary-total-card {
  background: color-mix(in srgb, var(--oa-gold-full) 6%, var(--oa-bg-2));
  border: 1px solid color-mix(in srgb, var(--oa-gold-full) 30%, transparent);
  border-radius: var(--oa-radius-xl);
  padding: 16px;
  text-align: center;
}

.summary-total-label {
  font-size: 10px;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: var(--oa-text-muted);
  margin: 0;
}

.summary-total-value {
  font-size: var(--oa-text-hero);
  font-weight: 700;
  color: var(--oa-gold-full);
  margin: 4px 0 0 0;
}

.summary-total-bar {
  width: 100%;
  height: 8px;
  border-radius: 4px;
  background: var(--oa-bg-3);
  margin-top: 8px;
  overflow: hidden;
}

.summary-total-bar-fill {
  height: 100%;
  border-radius: 4px;
  background: var(--oa-gold-full);
  transition: width 0.5s ease;
}

// === Navigation ===
.eval-nav {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding-top: 8px;
}

.nav-btn {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 16px;
  border-radius: 12px;
  font-size: var(--oa-text-sm);
  font-weight: 500;
  border: none;
  background: transparent;
  cursor: pointer;
  transition: all 0.15s ease;

  &.nav-prev {
    color: var(--oa-text-secondary);

    &:disabled {
      opacity: 0.3;
      cursor: not-allowed;
    }

    &:hover:not(:disabled) {
      background: color-mix(in srgb, var(--oa-bg-3) 30%, transparent);
    }
  }

  &.nav-next {
    color: var(--oa-gold-full);

    &:hover {
      background: color-mix(in srgb, var(--oa-gold-full) 8%, transparent);
    }
  }
}
</style>
